import type { UserPublic } from '@repo/types/users';
import type { ComponentProps, ReactNode } from 'react';

import { useCreateChat } from '@repo/api/chat/useCreateChat';
import { cn } from '@repo/lib/cn';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { getUserLvl } from '@repo/lib/user-lvl/getUserLvl';
import { MessagesSquareIcon, User } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Skeleton } from '@/components/ui/skeleton';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useCredentials } from '@/store/auth-store';
import { getAvatar } from '@/utils/getAvatar';

interface MemberSectionProps {
  label?: string;
  children: ReactNode;
  className?: string;
  labelClassName?: string;
  contentClassName?: string;
}

export function MemberSection(memberSectionProps: MemberSectionProps) {
  const { label, children, className, labelClassName, contentClassName } = memberSectionProps;

  return (
    <div className={cn('space-y-2', className)}>
      {label && <p className={cn('text-text-tertiary text-sm', labelClassName)}>{label}</p>}
      <div className={cn('flex-1 overflow-y-auto', contentClassName)}>{children}</div>
    </div>
  );
}

interface MemberSectionItemProps extends ComponentProps<'div'> {
  user: UserPublic;
  chatId?: number;
}

export function MemberSectionItem(memberSectionItemProps: MemberSectionItemProps) {
  const { user, chatId, className, ...props } = memberSectionItemProps;
  const navigate = useNavigate();
  const { id: currentUserId } = useCredentials();
  const { mutateAsync, isPending } = useCreateChat();

  const { avatarURL, name, surname, lvl } = user;

  const handleOpenChat = async () => {
    if (chatId) {
      navigate(`/chat/${chatId}`);
      return;
    }
    await mutateAsync({ userIds: [user.id] })
      .then((chat) => {
        navigate(`/chat/${chat.id}`);
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  return (
    <div className={cn('group flex items-center justify-between gap-3 overflow-hidden rounded-md py-2', className)} {...props}>
      <Avatar size={'md'}>
        <AvatarImage src={getAvatar(avatarURL)} alt={'avatar'} />
        <AvatarFallback>
          <User />
        </AvatarFallback>
      </Avatar>
      <div className={'flex w-full flex-col gap-0.5 overflow-hidden'}>
        <p className={'text-text-secondary w-full truncate text-sm'}>
          {name} {surname}
        </p>
        <small className={'text-text-tertiary text-xs'}>{getUserLvl(lvl)}</small>
      </div>
      {currentUserId !== user.id && (
        <Button size={'sm'} variant={'outline'} disabled={isPending} onClick={handleOpenChat}>
          <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
            <MessagesSquareIcon />
          </LoadingSwap>
        </Button>
      )}
    </div>
  );
}

export function MemberSectionItemSkeleton(memberSectionItemSkeletonProps: ComponentProps<'div'>) {
  const { className, ...props } = memberSectionItemSkeletonProps;
  return (
    <div className={cn('flex items-center gap-3 rounded-sm py-2', className)} {...props}>
      <Skeleton className="size-10 rounded-md" />
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-8 w-8 rounded-md" />
    </div>
  );
}
